import { useGetOrderCurrency } from '@src/components/OMS/useGetOrderCurrency';
import {
  Flex,
  MaxOrderSizeDialogs,
  MixpanelEvent,
  MixpanelEventSource,
  type OrderIntent,
  type Security,
  type SideEnum,
  Tab,
  TabList,
  type TabProps,
  TabSize,
  Tabs,
  useMixpanel,
  useTabs,
} from '@talos/kyoko';
import { useCallback, useMemo } from 'react';
import { PriceIncrement } from '../../../../components/Ladder/PriceIncrement';
import { useHandleClickMarketPrice } from '../../../../components/MarketDataCard/useHandleClickMarketPrice';
import { OrderBook } from '../../../../components/OrderBook/OrderBook';
import { useDeepDiveContext } from '../../../../providers/DeepDiveProvider';
import { useDisplaySettings } from '../../../../providers/DisplaySettingsProvider';
import { TradeHistory } from '../TradeHistory/TradeHistory';
import { PriceIncrementWrapper } from './styles';

interface AsideParams {
  security: Security;
}

const TABS: TabProps[] = [
  {
    label: 'Order Book',
  },
  {
    label: 'Trade History',
  },
];

export const Aside = ({ security }: AsideParams) => {
  const mixpanel = useMixpanel();
  const {
    selectedMarkets,
    selectedMarketAccounts,
    selectedExchangeMarkets,
    setShowCumQty,
    setPriceIncrement,
    showCumQty,
    priceIncrement: tabPriceIncrement,
    selectedAggregation,
  } = useDeepDiveContext();
  const { showAllInPrices, showFirmLiquidity } = useDisplaySettings();
  const { getOrderCurrency } = useGetOrderCurrency();

  const handleCumQtyClick = useCallback(
    (showCumQty: boolean) => {
      mixpanel.track(MixpanelEvent.ChangeQuantityToggle);
      setShowCumQty(showCumQty);
    },
    [mixpanel, setShowCumQty]
  );

  const onSelectTab = useCallback(
    (index: number, items?: TabProps[] | undefined) => {
      const item = items?.at(index);
      if (item && item.label === 'Trade History') {
        mixpanel.track(MixpanelEvent.ViewTradeHistory);
      }
    },
    [mixpanel]
  );

  const tabs = useTabs({
    initialItems: TABS,
    onSelect: onSelectTab,
  });
  const priceIncrement = useMemo(
    () => tabPriceIncrement ?? security.DefaultPriceIncrement,
    [tabPriceIncrement, security.DefaultPriceIncrement]
  );
  const { MinPriceIncrement } = security;

  const handleIncrementChange = useCallback(
    (index: number, newIncrement: string) => {
      mixpanel.track(MixpanelEvent.ChangePriceBucketing);
      // Validate new increment is greater than the obligated minimum
      const priceIncrement = newIncrement < MinPriceIncrement ? MinPriceIncrement : newIncrement;
      // save selected price increment for given DeepDive tab
      setPriceIncrement(priceIncrement);
    },
    [mixpanel, MinPriceIncrement, setPriceIncrement]
  );

  const { onClickPrice, clickToTradePreviewOrder, onCancelActive, maxOrderSizeDialogs } = useHandleClickMarketPrice({
    symbol: security.Symbol,
    marketAccounts: selectedMarketAccounts,
    aggregation: selectedAggregation,

    unifiedLiquidity: undefined,
    subAccount: undefined,
    defaultSubAccountId: undefined,
    markets: selectedMarkets,
    orderPresetID: undefined,
  });

  const handleRowClick = useCallback(
    ({ price, side, quantity, intent }: { price: string; side: SideEnum; quantity: string; intent: OrderIntent }) => {
      onClickPrice({
        values: {
          price,
          currency: getOrderCurrency(security, side),
          side,
          orderQty: quantity,
          intent,
        },
        source: MixpanelEventSource.OrderBook,
        activateOrder: true,
      });
    },
    [onClickPrice, security, getOrderCurrency]
  );

  return (
    <Flex
      flexDirection="column"
      maxWidth="460px"
      minWidth="340px"
      w="33%"
      gap="spacing4"
      justifyContent="flex-start"
      overflow="hidden"
      position="relative"
      background="backgroundContent"
    >
      <Tabs {...tabs} position="relative" size={TabSize.Large}>
        <TabList
          isBordered
          rightItems={
            tabs.selectedIndex === 0 && (
              <PriceIncrementWrapper>
                <PriceIncrement min={MinPriceIncrement} value={priceIncrement} onChange={handleIncrementChange} />
              </PriceIncrementWrapper>
            )
          }
        >
          {tabs.items.map((tab, index) => (
            <Tab key={index} {...tab} />
          ))}
        </TabList>
      </Tabs>
      <Flex flex="1 1 0%" overflow="hidden">
        {tabs.selectedIndex === 0 && (
          <OrderBook
            security={security}
            priceIncrement={priceIncrement}
            selectedMarkets={selectedMarkets}
            selectedMarketAccounts={selectedMarketAccounts}
            onRowClick={handleRowClick}
            onMouseLeaveRow={onCancelActive}
            onCumQtyClick={handleCumQtyClick}
            showCumQty={showCumQty}
            showAllInPrices={showAllInPrices}
            showFirmLiquidity={showFirmLiquidity}
            clickToTradePreviewOrder={clickToTradePreviewOrder}
          />
        )}
        {tabs.selectedIndex === 1 && <TradeHistory security={security} selectedMarkets={selectedExchangeMarkets} />}
      </Flex>
      <MaxOrderSizeDialogs {...maxOrderSizeDialogs} />
    </Flex>
  );
};
